/**
 * CarePlanAcknowledgementDialog — Phase 3 multi-stakeholder text-based ack.
 *
 * Per user direction: name + role + timestamp + ack checkbox + optional
 * witness. NO image signature capture in this phase.
 *
 * Only rendered when `care_plan_v2_enabled` is ON.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
// P3 — show the Staff Shift Brief above the ack form when the
// structured flag is ON and a shift_id is passed through. The brief
// itself enforces RBAC + redaction server-side.
import { StaffShiftBriefView } from './structured/StaffShiftBriefView';

const structuredFlagOn = () => {
  try { return !!(JSON.parse(localStorage.getItem('gc360_feature_flags') || '{}')).care_plan_v2_structured_matrix_enabled; }
  catch { return false; }
};

const ROLES = ['PARTICIPANT', 'FAMILY', 'SUPPORT_COORDINATOR', 'STAFF', 'GUARDIAN'];

export function CarePlanAcknowledgementDialog({ open, onOpenChange, plan, shiftId, onSubmit }) {
  const [signerName, setSignerName] = useState('');
  const [role, setRole] = useState('STAFF');
  const [ackChecked, setAckChecked] = useState(false);
  const [witnessName, setWitnessName] = useState('');
  const [witnessRole, setWitnessRole] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setSignerName(''); setRole('STAFF'); setAckChecked(false);
    setWitnessName(''); setWitnessRole(''); setSubmitting(false);
  };

  const submit = async () => {
    if (!signerName.trim() || !ackChecked) return;
    setSubmitting(true);
    try {
      await onSubmit({
        signer_name: signerName.trim(),
        role,
        ack_checkbox: true,
        witness_name: witnessName.trim() || undefined,
        witness_role: witnessRole.trim() || undefined,
      });
      reset();
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent data-testid="care-plan-ack-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Acknowledge Care Plan</DialogTitle>
        </DialogHeader>

        {structuredFlagOn() && shiftId && (
          <div className="border-b pb-3 mb-3 max-h-[40vh] overflow-y-auto" data-testid="ack-dialog-staff-brief">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Pre-shift brief (review before acknowledging)
            </p>
            <StaffShiftBriefView shiftId={shiftId} />
          </div>
        )}

        <div className="space-y-3">
          {plan && (
            <p className="text-xs text-muted-foreground">
              {plan.plan_no} · v{plan.version} · {plan.title}
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Signer name *</Label>
              <Input
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="e.g. Jane Doe"
                className="h-8 text-sm"
                data-testid="ack-signer-name"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Role *</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="h-8 text-sm" data-testid="ack-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => (
                    <SelectItem key={r} value={r}>{r.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={ackChecked}
              onChange={(e) => setAckChecked(e.target.checked)}
              data-testid="ack-checkbox"
              className="rounded"
            />
            I confirm I have read and understood this care plan.
          </label>

          <div className="grid grid-cols-2 gap-3 border-t pt-3">
            <div className="space-y-1">
              <Label className="text-xs">Witness name (optional)</Label>
              <Input
                value={witnessName}
                onChange={(e) => setWitnessName(e.target.value)}
                className="h-8 text-sm"
                data-testid="ack-witness-name"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Witness role (optional)</Label>
              <Input
                value={witnessRole}
                onChange={(e) => setWitnessRole(e.target.value)}
                placeholder="e.g. Coordinator"
                className="h-8 text-sm"
                data-testid="ack-witness-role"
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
          <Button
            disabled={!signerName.trim() || !ackChecked || submitting}
            onClick={submit}
            data-testid="ack-submit"
          >
            {submitting ? 'Saving…' : 'Acknowledge'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
